"use client";
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { PRODUCTS as INITIAL_PRODUCTS, Product, getStockLabel, formatPrice } from '@/lib/store';
import { Review } from '@/app/product-detail/page';

type OrderStatus = 'pending' | 'confirmed' | 'preparing' | 'out_for_delivery' | 'delivered';

interface Order {
  orderNumber: string;
  fullName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  location: 'accra' | 'outside';
  paymentMethod: 'pay_on_delivery' | 'mobile_money';
  transactionId?: string;
  items: Array<{ productId: string; quantity: number; product: Product }>;
  subtotal: number;
  deliveryFee: number;
  total: number;
  status: OrderStatus;
  createdAt: string;
}

interface Notification {
  id: string;
  message: string;
  read: boolean;
  createdAt: string;
}

interface Coupon {
  id: string;
  code: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  expiryDate: string;
  minOrderAmount: number;
  usageLimit: number;
  usedCount: number;
  active: boolean;
  createdAt: string;
}

const STATUS_CONFIG: Record<OrderStatus, { label: string; className: string }> = {
  pending: { label: 'Pending', className: 'status-pending' },
  confirmed: { label: 'Confirmed', className: 'status-confirmed' },
  preparing: { label: 'Preparing', className: 'status-preparing' },
  out_for_delivery: { label: 'Out for Delivery', className: 'status-outfordelivery' },
  delivered: { label: 'Delivered', className: 'status-delivered' },
};

// Admin credentials (master)
const ADMIN_EMAIL = 'komla0905@gmail.com';
const ADMIN_PASS = 'CakePalace@2025!';
const ADMIN_CREDS_KEY = 'cakepalace_admin_creds';

const DEFAULT_CATEGORIES = ['Decorating Tools', 'Equipment', 'Ingredients', 'Bakeware', 'Packaging'];

const REVIEWS_KEY = 'cakepalace_reviews';
const COUPONS_KEY = 'cakepalace_coupons';

export default function AdminDashboardPage() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'products' | 'orders' | 'inventory' | 'reviews' | 'coupons' | 'settings'>('overview');
  const [products, setProducts] = useState<Product[]>(INITIAL_PRODUCTS);
  const [orders, setOrders] = useState<Order[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productForm, setProductForm] = useState<Partial<Product>>({});
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [orderFilter, setOrderFilter] = useState<OrderStatus | 'all'>('all');
  const [categories, setCategories] = useState<string[]>(DEFAULT_CATEGORIES);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [imageUploadMode, setImageUploadMode] = useState<'url' | 'upload'>('url');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Admin credentials state (loaded from localStorage or defaults)
  const [adminCreds, setAdminCreds] = useState({ username: ADMIN_EMAIL, password: ADMIN_PASS });

  // Settings form state
  const [settingsSection, setSettingsSection] = useState<'account' | 'password'>('account');
  const [accountForm, setAccountForm] = useState({ newUsername: '', currentPasswordForAccount: '' });
  const [accountError, setAccountError] = useState('');
  const [accountSuccess, setAccountSuccess] = useState('');
  const [accountLoading, setAccountLoading] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [showAccountCurrentPass, setShowAccountCurrentPass] = useState(false);

  // Reviews state
  const [allReviews, setAllReviews] = useState<Review[]>([]);
  const [reviewProductFilter, setReviewProductFilter] = useState<string>('all');
  const [reviewStatusFilter, setReviewStatusFilter] = useState<'all' | 'pending' | 'approved'>('all');

  // Coupons state
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [couponForm, setCouponForm] = useState<Partial<Coupon>>({
    discountType: 'percentage',
    discountValue: 0,
    minOrderAmount: 0,
    usageLimit: 100,
    active: true,
  });
  const [couponFormError, setCouponFormError] = useState('');
  const [deleteCouponConfirm, setDeleteCouponConfirm] = useState<string | null>(null);
  const [copiedCouponId, setCopiedCouponId] = useState<string | null>(null);

  useEffect(() => {
    // Check session
    const session = sessionStorage.getItem('cp_admin');
    if (session === 'true') setLoggedIn(true);

    // Load admin credentials
    try {
      const storedCreds = JSON.parse(localStorage.getItem(ADMIN_CREDS_KEY) || 'null');
      if (storedCreds?.username && storedCreds?.password) {
        setAdminCreds(storedCreds);
      }
    } catch { /* no-op */ }

    // Load data
    try {
      const storedOrders: Order[] = JSON.parse(localStorage.getItem('cakepalace_orders') || '[]');
      setOrders(storedOrders);
      const storedNotifs: Notification[] = JSON.parse(localStorage.getItem('cakepalace_notifications') || '[]');
      setNotifications(storedNotifs);
      const storedCategories: string[] = JSON.parse(localStorage.getItem('cakepalace_categories') || 'null') || DEFAULT_CATEGORIES;
      setCategories(storedCategories);
      const storedReviews: Review[] = JSON.parse(localStorage.getItem(REVIEWS_KEY) || '[]');
      setAllReviews(storedReviews);
      const storedCoupons: Coupon[] = JSON.parse(localStorage.getItem(COUPONS_KEY) || '[]');
      setCoupons(storedCoupons);
    } catch { /* no-op */ }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);
    await new Promise(r => setTimeout(r, 800));
    const currentCreds = (() => {
      try {
        const stored = JSON.parse(localStorage.getItem(ADMIN_CREDS_KEY) || 'null');
        return stored?.username && stored?.password ? stored : { username: ADMIN_EMAIL, password: ADMIN_PASS };
      } catch { return { username: ADMIN_EMAIL, password: ADMIN_PASS }; }
    })();
    if (loginForm.username === currentCreds.username && loginForm.password === currentCreds.password) {
      sessionStorage.setItem('cp_admin', 'true');
      setLoggedIn(true);
      setLoginError('');
    } else {
      setLoginError('Invalid username or password.');
    }
    setLoginLoading(false);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('cp_admin');
    setLoggedIn(false);
  };

  const markAllRead = () => {
    const updated = notifications.map(n => ({ ...n, read: true }));
    setNotifications(updated);
    localStorage.setItem('cakepalace_notifications', JSON.stringify(updated));
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const updateOrderStatus = (orderNumber: string, status: OrderStatus) => {
    const updated = orders.map(o => {
      if (o.orderNumber !== orderNumber) return o;
      const newOrder = { ...o, status };
      // If delivered, reduce stock
      if (status === 'delivered') {
        o.items.forEach(item => {
          const p = products.find(p => p.id === item.productId);
          if (p) {
            setProducts(prev => prev.map(prod =>
              prod.id === item.productId
                ? { ...prod, stock: Math.max(0, prod.stock - item.quantity) }
                : prod
            ));
          }
        });
      }
      return newOrder;
    });
    setOrders(updated);
    localStorage.setItem('cakepalace_orders', JSON.stringify(updated));
    if (selectedOrder?.orderNumber === orderNumber) {
      setSelectedOrder(updated.find(o => o.orderNumber === orderNumber) || null);
    }
  };

  const openAddProduct = () => {
    setEditingProduct(null);
    setProductForm({ stock: 0, featured: false, category: categories[0] || 'Decorating Tools', price: 0 });
    setImagePreview('');
    setImageUploadMode('url');
    setShowProductModal(true);
  };

  const openEditProduct = (product: Product) => {
    setEditingProduct(product);
    setProductForm({ ...product });
    setImagePreview(product.image || '');
    setImageUploadMode('url');
    setShowProductModal(true);
  };

  const saveProduct = () => {
    if (!productForm.name || !productForm.price) return;
    if (editingProduct) {
      setProducts(prev => prev.map(p => p.id === editingProduct.id ? { ...p, ...productForm } as Product : p));
    } else {
      const newProd: Product = {
        id: Date.now().toString(),
        name: productForm.name || '',
        price: productForm.price || 0,
        description: productForm.description || '',
        shortDescription: productForm.shortDescription || '',
        image: productForm.image || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80',
        category: productForm.category || 'Decorating Tools',
        stock: productForm.stock || 0,
        featured: productForm.featured || false,
        sku: `SKU-${Date.now().toString().slice(-6)}`,
      };
      setProducts(prev => [newProd, ...prev]);
    }
    setShowProductModal(false);
  };

  const deleteProduct = (id: string) => {
    setProducts(prev => prev.filter(p => p.id !== id));
    setDeleteConfirm(null);
  };

  const toggleOutOfStock = (id: string) => {
    setProducts(prev => prev.map(p => p.id === id ? { ...p, stock: p.stock === 0 ? 10 : 0 } : p));
  };

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      setImagePreview(dataUrl);
      setProductForm(f => ({ ...f, image: dataUrl }));
    };
    reader.readAsDataURL(file);
  };

  const addCategory = () => {
    const trimmed = newCategoryName.trim();
    if (!trimmed || categories.includes(trimmed)) return;
    const updated = [...categories, trimmed];
    setCategories(updated);
    localStorage.setItem('cakepalace_categories', JSON.stringify(updated));
    setNewCategoryName('');
  };

  const deleteCategory = (cat: string) => {
    if (DEFAULT_CATEGORIES.includes(cat)) return;
    const updated = categories.filter(c => c !== cat);
    setCategories(updated);
    localStorage.setItem('cakepalace_categories', JSON.stringify(updated));
  };

  // Coupon helpers
  const generateCouponCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = 'CAKE';
    for (let i = 0; i < 6; i++) code += chars[Math.floor(Math.random() * chars.length)];
    return code;
  };

  const saveCoupon = () => {
    setCouponFormError('');
    if (!couponForm.code?.trim()) { setCouponFormError('Coupon code is required.'); return; }
    if (!couponForm.discountValue || couponForm.discountValue <= 0) { setCouponFormError('Discount value must be greater than 0.'); return; }
    if (couponForm.discountType === 'percentage' && couponForm.discountValue > 100) { setCouponFormError('Percentage discount cannot exceed 100%.'); return; }
    if (!couponForm.expiryDate) { setCouponFormError('Expiry date is required.'); return; }
    const codeUpper = couponForm.code.trim().toUpperCase();
    const duplicate = coupons.find(c => c.code === codeUpper);
    if (duplicate) { setCouponFormError('A coupon with this code already exists.'); return; }

    const newCoupon: Coupon = {
      id: Date.now().toString(),
      code: codeUpper,
      discountType: couponForm.discountType || 'percentage',
      discountValue: couponForm.discountValue || 0,
      expiryDate: couponForm.expiryDate || '',
      minOrderAmount: couponForm.minOrderAmount || 0,
      usageLimit: couponForm.usageLimit || 100,
      usedCount: 0,
      active: couponForm.active !== false,
      createdAt: new Date().toISOString(),
    };
    const updated = [newCoupon, ...coupons];
    setCoupons(updated);
    localStorage.setItem(COUPONS_KEY, JSON.stringify(updated));
    setShowCouponModal(false);
    resetCouponForm();
  };

  const resetCouponForm = () => {
    setCouponForm({ discountType: 'percentage', discountValue: 0, minOrderAmount: 0, usageLimit: 100, active: true });
    setCouponFormError('');
  };

  const toggleCouponActive = (id: string) => {
    const updated = coupons.map(c => c.id === id ? { ...c, active: !c.active } : c);
    setCoupons(updated);
    localStorage.setItem(COUPONS_KEY, JSON.stringify(updated));
  };

  const deleteCoupon = (id: string) => {
    const updated = coupons.filter(c => c.id !== id);
    setCoupons(updated);
    localStorage.setItem(COUPONS_KEY, JSON.stringify(updated));
    setDeleteCouponConfirm(null);
  };

  const copyCouponCode = (coupon: Coupon) => {
    navigator.clipboard.writeText(coupon.code).catch(() => {});
    setCopiedCouponId(coupon.id);
    setTimeout(() => setCopiedCouponId(null), 2000);
  };

  const shareCoupon = (coupon: Coupon, platform: 'whatsapp' | 'facebook' | 'twitter') => {
    const discountText = coupon.discountType === 'percentage'
      ? `${coupon.discountValue}% OFF`
      : `₵${coupon.discountValue} OFF`;
    const expiryText = new Date(coupon.expiryDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    const minOrderText = coupon.minOrderAmount > 0 ? ` on orders above ₵${coupon.minOrderAmount}` : '';
    const shopUrl = 'https://cakepalace4668.builtwithrocket.new/product-shop';
    const message = `🎂 CakePalace Special Offer!\n\nGet ${discountText}${minOrderText} on your next order!\n\n🏷️ Use code: ${coupon.code}\n📅 Valid until: ${expiryText}\n\nShop now: ${shopUrl}`;

    let url = '';
    if (platform === 'whatsapp') {
      url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    } else if (platform === 'facebook') {
      url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shopUrl)}&quote=${encodeURIComponent(message)}`;
    } else if (platform === 'twitter') {
      const tweet = `🎂 CakePalace Special Offer! Get ${discountText}${minOrderText}! Use code: ${coupon.code} (valid until ${expiryText}) 👉 ${shopUrl}`;
      url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweet)}`;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const isCouponExpired = (coupon: Coupon) => new Date(coupon.expiryDate) < new Date();

  // Stats
  const totalRevenue = orders.filter(o => o.status === 'delivered').reduce((s, o) => s + o.total, 0);
  const pendingOrders = orders.filter(o => o.status === 'pending').length;
  const filteredOrders = orderFilter === 'all' ? orders : orders.filter(o => o.status === orderFilter);
  const lowStockProducts = products.filter(p => p.stock > 0 && p.stock <= 5);
  const outOfStockProducts = products.filter(p => p.stock === 0);
  const pendingReviewsCount = allReviews.filter(r => !r.approved).length;
  const activeCouponsCount = coupons.filter(c => c.active && !isCouponExpired(c)).length;

  // Password strength calculator
  const passwordStrength = (() => {
    const pw = passwordForm.newPassword;
    if (!pw) return { label: 'Weak', color: 'bg-red-400', width: '0%' };
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 1) return { label: 'Weak', color: 'bg-red-400', width: '25%' };
    if (score === 2) return { label: 'Fair', color: 'bg-amber-400', width: '50%' };
    if (score === 3) return { label: 'Good', color: 'bg-blue-400', width: '75%' };
    return { label: 'Strong', color: 'bg-green-500', width: '100%' };
  })();

  const handleSaveAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setAccountError('');
    setAccountSuccess('');
    const { newUsername, currentPasswordForAccount } = accountForm;
    if (!newUsername.trim()) { setAccountError('Please enter a new username.'); return; }
    if (!/^[a-zA-Z0-9_]{3,}$/.test(newUsername.trim())) { setAccountError('Username must be at least 3 characters and contain only letters, numbers, and underscores.'); return; }
    if (!currentPasswordForAccount) { setAccountError('Please enter your current password.'); return; }
    if (currentPasswordForAccount !== adminCreds.password) { setAccountError('Current password is incorrect.'); return; }
    setAccountLoading(true);
    await new Promise(r => setTimeout(r, 700));
    const updated = { ...adminCreds, username: newUsername.trim() };
    setAdminCreds(updated);
    localStorage.setItem(ADMIN_CREDS_KEY, JSON.stringify(updated));
    setAccountSuccess('Username updated successfully!');
    setAccountForm({ newUsername: '', currentPasswordForAccount: '' });
    setAccountLoading(false);
  };

  const handleSavePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    const { currentPassword, newPassword, confirmPassword } = passwordForm;
    if (!currentPassword) { setPasswordError('Please enter your current password.'); return; }
    if (currentPassword !== adminCreds.password) { setPasswordError('Current password is incorrect.'); return; }
    if (!newPassword) { setPasswordError('Please enter a new password.'); return; }
    if (newPassword.length < 8) { setPasswordError('New password must be at least 8 characters.'); return; }
    if (!/[A-Z]/.test(newPassword)) { setPasswordError('New password must contain at least one uppercase letter.'); return; }
    if (!/[0-9]/.test(newPassword)) { setPasswordError('New password must contain at least one number.'); return; }
    if (newPassword !== confirmPassword) { setPasswordError('Passwords do not match.'); return; }
    setPasswordLoading(true);
    await new Promise(r => setTimeout(r, 700));
    const updated = { ...adminCreds, password: newPassword };
    setAdminCreds(updated);
    localStorage.setItem(ADMIN_CREDS_KEY, JSON.stringify(updated));
    setPasswordSuccess('Password updated successfully!');
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordLoading(false);
  };

  // LOGIN SCREEN
  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <AppLogo size={40} />
              <span className="font-display text-2xl font-semibold text-foreground">CakePalace</span>
            </div>
            <h1 className="font-display text-2xl font-semibold text-foreground">Admin Login</h1>
            <p className="text-muted text-sm mt-1">Enter your credentials to access the dashboard</p>
          </div>

          <div className="bg-white rounded-md border border-border p-8 shadow-md">
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
                <input
                  type="text"
                  value={loginForm.username}
                  onChange={e => setLoginForm(f => ({ ...f, username: e.target.value }))}
                  placeholder=""
                  className="form-input"
                  autoComplete="username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Password</label>
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))}
                  placeholder="••••••••••"
                  className="form-input"
                  autoComplete="current-password"
                />
              </div>

              {loginError && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-sm px-3 py-2.5 text-sm text-danger">
                  <Icon name="ExclamationCircleIcon" size={16} />
                  {loginError}
                </div>
              )}

              <button
                type="submit"
                disabled={loginLoading}
                className="btn-primary w-full py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {loginLoading ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Signing in...</>
                ) : (
                  <><Icon name="LockClosedIcon" size={16} /> Sign In</>
                )}
              </button>
            </form>

          </div>

          <div className="text-center mt-6">
            <Link href="/product-shop" className="text-sm text-muted hover:text-primary transition-colors flex items-center justify-center gap-1">
              <Icon name="ArrowLeftIcon" size={14} /> Back to Shop
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // DASHBOARD
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Admin Header */}
      <header className="bg-white border-b border-border sticky top-0 z-50">
        <div className="max-w-container mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <AppLogo size={32} />
              <span className="font-display font-semibold text-foreground hidden sm:block">CakePalace</span>
            </div>
            <span className="text-muted-light text-sm hidden sm:block">|</span>
            <span className="text-sm font-medium text-primary hidden sm:block">Admin Dashboard</span>
          </div>

          <div className="flex items-center gap-2">
            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => { setShowNotifs(!showNotifs); markAllRead(); }}
                className="relative flex items-center justify-center w-9 h-9 rounded-sm text-muted hover:text-foreground hover:bg-accent transition-colors"
              >
                <Icon name="BellIcon" size={20} />
                {unreadCount > 0 && (
                  <span className="cart-bubble absolute -top-0.5 -right-0.5">{unreadCount}</span>
                )}
              </button>

              {showNotifs && (
                <div className="absolute right-0 top-11 w-72 sm:w-80 bg-white border border-border rounded-md shadow-lg z-50 animate-scale-in max-w-[calc(100vw-2rem)]">
                  <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                    <span className="font-semibold text-sm text-foreground">Notifications</span>
                    <button onClick={() => setShowNotifs(false)} className="text-muted-light hover:text-foreground">
                      <Icon name="XMarkIcon" size={16} />
                    </button>
                  </div>
                  <div className="max-h-64 sm:max-h-72 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <p className="text-sm text-muted text-center py-6">No notifications</p>
                    ) : (
                      notifications.slice(0, 10).map(n => (
                        <div key={n.id} className={`px-4 py-3 border-b border-border last:border-0 ${!n.read ? 'bg-amber-50' : ''}`}>
                          <p className="text-sm text-foreground">{n.message}</p>
                          <p className="text-xs text-muted mt-0.5">{new Date(n.createdAt).toLocaleString()}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <Link href="/product-shop" className="text-xs text-muted hover:text-primary transition-colors px-2 py-1 rounded-sm hover:bg-accent flex items-center gap-1">
              <Icon name="ArrowTopRightOnSquareIcon" size={14} />
              <span className="hidden sm:inline">View Shop</span>
            </Link>

            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-xs text-muted hover:text-danger transition-colors px-2 sm:px-3 py-2 rounded-sm hover:bg-red-50"
            >
              <Icon name="ArrowRightOnRectangleIcon" size={16} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-container mx-auto px-4 sm:px-6 py-6 sm:py-8 w-full">
        {/* Tab Navigation */}
        <div className="mb-6 sm:mb-8 -mx-4 sm:mx-0 px-4 sm:px-0">
          <div className="flex gap-1 bg-white border border-border rounded-sm p-1 overflow-x-auto scrollbar-hide">
            {[
              { id: 'overview', label: 'Overview', icon: 'ChartBarIcon' },
              { id: 'orders', label: `Orders${orders.length > 0 ? ` (${orders.length})` : ''}`, icon: 'ClipboardDocumentListIcon' },
              { id: 'products', label: 'Products', icon: 'ArchiveBoxIcon' },
              { id: 'inventory', label: 'Inventory', icon: 'CubeIcon' },
              { id: 'reviews', label: `Reviews${pendingReviewsCount > 0 ? ` (${pendingReviewsCount})` : ''}`, icon: 'StarIcon' },
              { id: 'coupons', label: `Coupons${activeCouponsCount > 0 ? ` (${activeCouponsCount})` : ''}`, icon: 'TagIcon' },
              { id: 'settings', label: 'Settings', icon: 'Cog6ToothIcon' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-4 py-2 rounded-sm text-xs sm:text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.id
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-muted hover:text-foreground hover:bg-accent'
                }`}
              >
                <Icon name={tab.icon as Parameters<typeof Icon>[0]['name']} size={14} />
                <span className="hidden xs:inline sm:inline">{tab.label}</span>
                <span className="xs:hidden sm:hidden">
                  {tab.id === 'overview' ? 'Home' :
                   tab.id === 'orders' ? `Orders${orders.length > 0 ? ` (${orders.length})` : ''}` :
                   tab.id === 'products' ? 'Prods' :
                   tab.id === 'inventory' ? 'Stock' :
                   tab.id === 'reviews' ? `Rev${pendingReviewsCount > 0 ? ` (${pendingReviewsCount})` : ''}` :
                   tab.id === 'coupons' ? `Coup${activeCouponsCount > 0 ? ` (${activeCouponsCount})` : ''}` :
                   'Config'}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div className="animate-fade-in">
            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[
                { label: 'Total Orders', value: orders.length.toString(), icon: 'ClipboardDocumentListIcon', color: 'text-primary', bg: 'bg-amber-50' },
                { label: 'Revenue (Delivered)', value: formatPrice(totalRevenue), icon: 'BanknotesIcon', color: 'text-success', bg: 'bg-green-50' },
                { label: 'Pending Orders', value: pendingOrders.toString(), icon: 'ClockIcon', color: 'text-amber-600', bg: 'bg-amber-50' },
                { label: 'Total Products', value: products.length.toString(), icon: 'ArchiveBoxIcon', color: 'text-blue-600', bg: 'bg-blue-50' },
              ].map((stat, i) => (
                <div key={i} className="bg-white rounded-md border border-border p-5">
                  <div className={`w-10 h-10 rounded-sm ${stat.bg} flex items-center justify-center mb-3`}>
                    <Icon name={stat.icon as Parameters<typeof Icon>[0]['name']} size={20} className={stat.color} />
                  </div>
                  <p className="text-2xl font-display font-semibold text-foreground">{stat.value}</p>
                  <p className="text-sm text-muted mt-0.5">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Recent orders + alerts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white rounded-md border border-border overflow-hidden">
                <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                  <h2 className="font-display text-base font-semibold text-foreground">Recent Orders</h2>
                  <button onClick={() => setActiveTab('orders')} className="text-xs text-primary hover:underline">View all</button>
                </div>
                {orders.length === 0 ? (
                  <div className="text-center py-10 text-muted text-sm">No orders yet</div>
                ) : (
                  <table className="admin-table w-full">
                    <thead>
                      <tr>
                        <th className="text-left">Order</th>
                        <th className="text-left">Customer</th>
                        <th className="text-left hidden sm:table-cell">Total</th>
                        <th className="text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.slice(0, 5).map(order => (
                        <tr key={order.orderNumber} className="cursor-pointer" onClick={() => { setSelectedOrder(order); setActiveTab('orders'); }}>
                          <td className="font-mono text-xs text-primary font-semibold">{order.orderNumber}</td>
                          <td className="font-medium text-sm">{order.fullName}</td>
                          <td className="hidden sm:table-cell text-sm font-semibold text-primary">{formatPrice(order.total)}</td>
                          <td>
                            <span className={`badge-stock ${STATUS_CONFIG[order.status].className}`}>
                              {STATUS_CONFIG[order.status].label}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Alerts */}
              <div className="bg-white rounded-md border border-border overflow-hidden">
                <div className="px-5 py-4 border-b border-border">
                  <h2 className="font-display text-base font-semibold text-foreground">Stock Alerts</h2>
                </div>
                <div className="p-4 space-y-3">
                  {outOfStockProducts.length === 0 && lowStockProducts.length === 0 ? (
                    <div className="text-center py-6">
                      <Icon name="CheckCircleIcon" size={32} className="text-success mx-auto mb-2" variant="solid" />
                      <p className="text-sm text-muted">All products are well stocked</p>
                    </div>
                  ) : (
                    <>
                      {outOfStockProducts.map(p => (
                        <div key={p.id} className="flex items-center gap-2 p-2.5 bg-red-50 rounded-sm border border-red-100">
                          <Icon name="ExclamationCircleIcon" size={16} className="text-danger flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-foreground line-clamp-1">{p.name}</p>
                            <p className="text-xs text-danger">Out of Stock</p>
                          </div>
                        </div>
                      ))}
                      {lowStockProducts.map(p => (
                        <div key={p.id} className="flex items-center gap-2 p-2.5 bg-amber-50 rounded-sm border border-amber-100">
                          <Icon name="ExclamationTriangleIcon" size={16} className="text-amber-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-foreground line-clamp-1">{p.name}</p>
                            <p className="text-xs text-amber-600">Only {p.stock} left</p>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ORDERS TAB */}
        {activeTab === 'orders' && (
          <div className="animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <h2 className="font-display text-2xl font-semibold text-foreground">Orders</h2>
              {/* Status filter */}
              <div className="flex flex-wrap gap-2">
                {(['all', 'pending', 'confirmed', 'preparing', 'out_for_delivery', 'delivered'] as const).map(s => (
                  <button
                    key={s}
                    onClick={() => setOrderFilter(s)}
                    className={`px-3 py-1.5 rounded-sm text-sm font-medium transition-all border ${
                      orderFilter === s
                        ? 'bg-primary text-white border-primary' :'bg-white border-border text-muted hover:border-primary hover:text-foreground'
                    }`}
                  >
                    {s === 'all' ? 'All' : STATUS_CONFIG[s].label}
                    {s !== 'all' && (
                      <span className="ml-1 opacity-70">({orders.filter(o => o.status === s).length})</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Orders list */}
              <div className="lg:col-span-2">
                {filteredOrders.length === 0 ? (
                  <div className="bg-white rounded-md border border-border text-center py-16">
                    <Icon name="ClipboardDocumentListIcon" size={40} className="text-muted-light mx-auto mb-3" />
                    <p className="text-sm text-muted">No orders found</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-md border border-border overflow-hidden">
                    <table className="admin-table w-full">
                      <thead>
                        <tr>
                          <th className="text-left">Order #</th>
                          <th className="text-left">Customer</th>
                          <th className="text-left hidden md:table-cell">Payment</th>
                          <th className="text-left hidden sm:table-cell">Total</th>
                          <th className="text-left">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOrders.map(order => (
                          <tr
                            key={order.orderNumber}
                            className={`cursor-pointer transition-colors ${selectedOrder?.orderNumber === order.orderNumber ? 'bg-amber-50' : ''}`}
                            onClick={() => setSelectedOrder(order)}
                          >
                            <td className="font-mono text-xs text-primary font-semibold">{order.orderNumber}</td>
                            <td>
                              <p className="text-sm font-medium text-foreground">{order.fullName}</p>
                              <p className="text-xs text-muted">{order.phone}</p>
                            </td>
                            <td className="hidden md:table-cell">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-sm ${
                                order.paymentMethod === 'pay_on_delivery' ?'bg-green-50 text-green-700' :'bg-amber-50 text-amber-700'
                              }`}>
                                {order.paymentMethod === 'pay_on_delivery' ? 'Pay on Delivery' : 'Mobile Money'}
                              </span>
                            </td>
                            <td className="hidden sm:table-cell text-sm font-semibold text-primary">{formatPrice(order.total)}</td>
                            <td>
                              <span className={`badge-stock ${STATUS_CONFIG[order.status].className}`}>
                                {STATUS_CONFIG[order.status].label}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Order detail panel */}
              <div className="lg:col-span-1">
                {selectedOrder ? (
                  <div className="bg-white rounded-md border border-border overflow-hidden sticky top-24 animate-scale-in">
                    <div className="px-5 py-4 border-b border-border bg-accent/40 flex items-center justify-between">
                      <div>
                        <p className="font-mono text-sm font-bold text-primary">{selectedOrder.orderNumber}</p>
                        <p className="text-xs text-muted">{new Date(selectedOrder.createdAt).toLocaleString()}</p>
                      </div>
                      <button onClick={() => setSelectedOrder(null)} className="text-muted-light hover:text-foreground">
                        <Icon name="XMarkIcon" size={18} />
                      </button>
                    </div>

                    <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
                      {/* Customer */}
                      <div>
                        <p className="text-xs font-semibold text-muted uppercase tracking-wider mb-2">Customer</p>
                        <p className="text-sm font-medium text-foreground">{selectedOrder.fullName}</p>
                        <p className="text-xs text-muted">{selectedOrder.email}</p>
                        <p className="text-xs text-muted">{selectedOrder.phone}</p>
                        <p className="text-xs text-muted mt-1">{selectedOrder.address}{selectedOrder.city && `, ${selectedOrder.city}`}</p>
                        <span className={`inline-block mt-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded-full px-2.5 py-1 font-medium flex items-center gap-1`}>
                          <Icon name="ShieldCheckIcon" size={12} />
                          Active
                        </span>
                      </div>

                      {/* Payment */}
                      <div>
                        <p className="text-xs font-semibold text-muted uppercase tracking-wider mb-2">Payment</p>
                        <p className="text-sm font-medium text-foreground">
                          {selectedOrder.paymentMethod === 'pay_on_delivery' ? 'Pay on Delivery' : 'Mobile Money'}
                        </p>
                        {selectedOrder.transactionId && (
                          <p className="text-xs font-mono text-muted mt-0.5">Ref: {selectedOrder.transactionId}</p>
                        )}
                      </div>

                      {/* Items */}
                      <div>
                        <p className="text-xs font-semibold text-muted uppercase tracking-wider mb-2">Items</p>
                        <div className="space-y-2">
                          {selectedOrder.items.map(item => (
                            <div key={item.productId} className="flex items-center gap-2">
                              <div className="w-9 h-9 rounded-sm overflow-hidden flex-shrink-0 bg-accent">
                                <AppImage src={item.product.image} alt={item.product.name} width={36} height={36} className="object-cover w-full h-full" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-foreground line-clamp-1">{item.product.name}</p>
                                <p className="text-xs text-muted">×{item.quantity}</p>
                              </div>
                              <p className="text-xs font-semibold text-primary">{formatPrice(item.product.price * item.quantity)}</p>
                            </div>
                          ))}
                        </div>
                        <div className="border-t border-border mt-3 pt-3 flex justify-between">
                          <span className="text-sm font-semibold text-foreground">Total</span>
                          <span className="text-sm font-display font-semibold text-primary">{formatPrice(selectedOrder.total)}</span>
                        </div>
                      </div>

                      {selectedOrder.notes && (
                        <div>
                          <p className="text-xs font-semibold text-muted uppercase tracking-wider mb-1">Notes</p>
                          <p className="text-sm text-muted bg-accent rounded-sm p-2">{selectedOrder.notes}</p>
                        </div>
                      )}

                      {/* Status update */}
                      <div>
                        <p className="text-xs font-semibold text-muted uppercase tracking-wider mb-2">Update Status</p>
                        <div className="space-y-2">
                          {(Object.keys(STATUS_CONFIG) as OrderStatus[]).map(status => (
                            <button
                              key={status}
                              onClick={() => updateOrderStatus(selectedOrder.orderNumber, status)}
                              className={`w-full flex items-center justify-between px-3 py-2 rounded-sm text-sm border transition-all ${
                                selectedOrder.status === status
                                  ? 'border-primary bg-amber-50 text-primary font-semibold' :'border-border text-muted hover:border-primary hover:text-foreground'
                              }`}
                            >
                              <span>{STATUS_CONFIG[status].label}</span>
                              {selectedOrder.status === status && (
                                <Icon name="CheckCircleIcon" size={16} className="text-primary" variant="solid" />
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-md border border-border text-center py-12 px-6">
                    <Icon name="CursorArrowRaysIcon" size={32} className="text-muted-light mx-auto mb-3" />
                    <p className="text-sm text-muted">Click an order to view details and update status</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* PRODUCTS TAB */}
        {activeTab === 'products' && (
          <div className="animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
              <h2 className="font-display text-2xl font-semibold text-foreground">Products</h2>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setShowCategoryManager(!showCategoryManager)}
                  className="btn-outline px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm flex items-center gap-1.5"
                >
                  <Icon name="TagIcon" size={15} /> <span className="hidden xs:inline">Manage </span>Categories
                </button>
                <button onClick={openAddProduct} className="btn-primary px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm flex items-center gap-1.5">
                  <Icon name="PlusIcon" size={15} variant="solid" /> Add Product
                </button>
              </div>
            </div>

            {/* Category Manager Panel */}
            {showCategoryManager && (
              <div className="bg-white rounded-md border border-border p-5 mb-6 animate-scale-in">
                <h3 className="font-display text-base font-semibold text-foreground mb-4">Manage Categories</h3>
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={e => setNewCategoryName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addCategory()}
                    placeholder="New category name..."
                    className="form-input"
                  />
                  <button
                    onClick={addCategory}
                    disabled={!newCategoryName.trim() || categories.includes(newCategoryName.trim())}
                    className="btn-primary px-4 py-2 text-sm flex items-center gap-1.5 disabled:opacity-50"
                  >
                    <Icon name="PlusIcon" size={14} variant="solid" /> Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {categories.map(cat => (
                    <div key={cat} className="flex items-center gap-1.5 bg-accent border border-border rounded-sm px-3 py-1.5">
                      <span className="text-sm text-foreground">{cat}</span>
                      {!DEFAULT_CATEGORIES.includes(cat) && (
                        <button
                          onClick={() => deleteCategory(cat)}
                          className="text-muted-light hover:text-danger transition-colors ml-1"
                          title="Remove category"
                        >
                          <Icon name="XMarkIcon" size={14} />
                        </button>
                      )}
                      {DEFAULT_CATEGORIES.includes(cat) && (
                        <span className="text-xs text-muted-light ml-1">(default)</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-white rounded-md border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="admin-table w-full min-w-[700px]">
                  <thead>
                    <tr>
                      <th className="text-left">Product</th>
                      <th className="text-left">Category</th>
                      <th className="text-left">Price</th>
                      <th className="text-left">Stock</th>
                      <th className="text-left">Featured</th>
                      <th className="text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map(product => {
                      const stockInfo = getStockLabel(product.stock);
                      return (
                        <tr key={product.id}>
                          <td>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-sm overflow-hidden flex-shrink-0 bg-accent">
                                <AppImage src={product.image} alt={product.name} width={40} height={40} className="object-cover w-full h-full" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-foreground line-clamp-1">{product.name}</p>
                                <p className="text-xs font-mono text-muted">{product.sku}</p>
                              </div>
                            </div>
                          </td>
                          <td className="text-sm text-muted">{product.category}</td>
                          <td className="text-sm font-semibold text-primary">{formatPrice(product.price)}</td>
                          <td>
                            <span className={`badge-stock ${stockInfo.className}`}>{product.stock === 0 ? 'Out of Stock' : `${product.stock} units`}</span>
                          </td>
                          <td>
                            {product.featured ? (
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-sm font-medium">Featured</span>
                            ) : (
                              <span className="text-xs text-muted-light">—</span>
                            )}
                          </td>
                          <td>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openEditProduct(product)}
                                className="w-8 h-8 flex items-center justify-center rounded-sm text-muted hover:text-primary hover:bg-amber-50 transition-colors"
                                title="Edit"
                              >
                                <Icon name="PencilSquareIcon" size={16} />
                              </button>
                              <button
                                onClick={() => setDeleteConfirm(product.id)}
                                className="w-8 h-8 flex items-center justify-center rounded-sm text-muted hover:text-danger hover:bg-red-50 transition-colors"
                                title="Delete"
                              >
                                <Icon name="TrashIcon" size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* INVENTORY TAB */}
        {activeTab === 'inventory' && (
          <div className="animate-fade-in">
            <h2 className="font-display text-2xl font-semibold text-foreground mb-6">Inventory Management</h2>

            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              {[
                { label: 'Total Products', value: products.length, icon: 'ArchiveBoxIcon', color: 'text-blue-600', bg: 'bg-blue-50' },
                { label: 'Low Stock (≤5)', value: lowStockProducts.length, icon: 'ExclamationTriangleIcon', color: 'text-amber-600', bg: 'bg-amber-50' },
                { label: 'Out of Stock', value: outOfStockProducts.length, icon: 'ExclamationCircleIcon', color: 'text-danger', bg: 'bg-red-50' },
              ].map((s, i) => (
                <div key={i} className="bg-white rounded-md border border-border p-5 flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-sm ${s.bg} flex items-center justify-center`}>
                    <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={22} className={s.color} />
                  </div>
                  <div>
                    <p className="text-2xl font-display font-semibold text-foreground">{s.value}</p>
                    <p className="text-sm text-muted">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-md border border-border overflow-hidden">
              <div className="px-5 py-4 border-b border-border">
                <h3 className="font-display text-base font-semibold text-foreground">Stock Levels</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="admin-table w-full min-w-[600px]">
                  <thead>
                    <tr>
                      <th className="text-left">Product</th>
                      <th className="text-left">Category</th>
                      <th className="text-left">Stock Level</th>
                      <th className="text-left">Status</th>
                      <th className="text-left">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products
                      .sort((a, b) => a.stock - b.stock)
                      .map(product => {
                        const stockInfo = getStockLabel(product.stock);
                        const pct = Math.min(100, (product.stock / 50) * 100);
                        return (
                          <tr key={product.id}>
                            <td>
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-sm overflow-hidden flex-shrink-0 bg-accent">
                                  <AppImage src={product.image} alt={product.name} width={32} height={32} className="object-cover w-full h-full" />
                                </div>
                                <p className="text-sm font-medium text-foreground line-clamp-1">{product.name}</p>
                              </div>
                            </td>
                            <td className="text-xs text-muted">{product.category}</td>
                            <td>
                              <div className="flex items-center gap-3">
                                <div className="flex-1 h-2 bg-border rounded-full overflow-hidden max-w-24">
                                  <div
                                    className={`h-full rounded-full transition-all ${
                                      product.stock === 0 ? 'bg-danger' :
                                      product.stock <= 5 ? 'bg-amber-400' : 'bg-success'
                                    }`}
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                                <span className="text-sm font-semibold text-foreground w-8">{product.stock}</span>
                              </div>
                            </td>
                            <td>
                              <span className={`badge-stock ${stockInfo.className}`}>{stockInfo.label}</span>
                            </td>
                            <td>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => toggleOutOfStock(product.id)}
                                  className={`text-xs px-2.5 py-1.5 rounded-sm font-medium border transition-all ${
                                    product.stock === 0
                                      ? 'bg-green-50 text-success border-green-200 hover:bg-green-100' :'bg-red-50 text-danger border-red-200 hover:bg-red-100'
                                  }`}
                                >
                                  {product.stock === 0 ? 'Mark In Stock' : 'Mark Out of Stock'}
                                </button>
                                <button
                                  onClick={() => openEditProduct(product)}
                                  className="text-xs px-2.5 py-1.5 rounded-sm font-medium border border-border text-muted hover:border-primary hover:text-primary transition-all"
                                >
                                  Edit Stock
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* REVIEWS TAB */}
        {activeTab === 'reviews' && (
          <div className="animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="font-display text-2xl font-semibold text-foreground">Reviews</h2>
                <p className="text-sm text-muted mt-0.5">
                  {allReviews.length} total · {allReviews.filter(r => r.approved).length} approved · {pendingReviewsCount} pending
                </p>
              </div>
              {/* Filters */}
              <div className="flex flex-wrap gap-2">
                <select
                  value={reviewProductFilter}
                  onChange={e => setReviewProductFilter(e.target.value)}
                  className="form-input py-1.5 text-sm w-auto"
                >
                  <option value="all">All Products</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                {(['all', 'pending', 'approved'] as const).map(s => (
                  <button
                    key={s}
                    onClick={() => setReviewStatusFilter(s)}
                    className={`px-3 py-1.5 rounded-sm text-sm font-medium transition-all border ${
                      reviewStatusFilter === s
                        ? 'bg-primary text-white border-primary' :'bg-white border-border text-muted hover:border-primary hover:text-foreground'
                    }`}
                  >
                    {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {(() => {
              const filtered = allReviews
                .filter(r => reviewProductFilter === 'all' || r.productId === reviewProductFilter)
                .filter(r => reviewStatusFilter === 'all' || (reviewStatusFilter === 'approved' ? r.approved : !r.approved))
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

              if (filtered.length === 0) {
                return (
                  <div className="bg-white rounded-md border border-border text-center py-16">
                    <Icon name="StarIcon" size={40} className="text-muted-light mx-auto mb-3" />
                    <p className="text-sm font-medium text-foreground mb-1">No reviews found</p>
                    <p className="text-xs text-muted">Reviews submitted by customers will appear here</p>
                  </div>
                );
              }

              return (
                <div className="space-y-3">
                  {filtered.map(review => {
                    const reviewProduct = products.find(p => p.id === review.productId);
                    return (
                      <div
                        key={review.id}
                        className={`bg-white rounded-md border transition-all ${
                          review.approved ? 'border-border' : 'border-amber-200 bg-amber-50/30'
                        }`}
                      >
                        <div className="p-5">
                          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                            {/* Customer info */}
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <span className="text-sm font-semibold text-primary">
                                  {review.customerName.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                  <span className="text-sm font-semibold text-foreground">{review.customerName}</span>
                                  <span className="text-xs text-muted">
                                    {new Date(review.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                  </span>
                                  {review.approved ? (
                                    <span className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded-full px-2 py-0.5 font-medium">
                                      <Icon name="CheckCircleIcon" size={11} />
                                      Approved
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5 font-medium">
                                      <Icon name="ClockIcon" size={11} />
                                      Pending
                                    </span>
                                  )}
                                </div>
                                {/* Stars */}
                                <div className="flex items-center gap-1 mb-2">
                                  {[1, 2, 3, 4, 5].map(star => (
                                    <svg
                                      key={star}
                                      className={`w-4 h-4 ${star <= review.rating ? 'text-amber-400' : 'text-gray-200'}`}
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                  ))}
                                  <span className="text-xs text-muted ml-1">{review.rating}/5</span>
                                </div>
                                <p className="text-sm text-muted leading-relaxed">{review.comment}</p>
                                {reviewProduct && (
                                  <p className="text-xs text-muted mt-2 flex items-center gap-1">
                                    <Icon name="ArchiveBoxIcon" size={11} />
                                    Product: <span className="font-medium text-foreground">{reviewProduct.name}</span>
                                  </p>
                                )}
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-2 sm:flex-col sm:items-end flex-shrink-0">
                              <button
                                onClick={() => toggleReviewApproval(review.id)}
                                className={`flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs font-semibold border transition-all ${
                                  review.approved
                                    ? 'bg-green-50 text-green-700 border-green-200 hover:bg-red-50 hover:text-danger hover:border-red-200' :'bg-amber-50 text-amber-700 border-amber-200 hover:bg-green-50 hover:text-green-700 hover:border-green-200'
                                }`}
                                title={review.approved ? 'Click to revoke approval' : 'Click to approve'}
                              >
                                <Icon name={review.approved ? 'CheckCircleIcon' : 'ClockIcon'} size={14} />
                                {review.approved ? 'Approved' : 'Approve'}
                              </button>
                              <button
                                onClick={() => deleteReview(review.id)}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs font-medium border border-border text-muted hover:bg-red-50 hover:text-danger hover:border-red-200 transition-all"
                                title="Delete review"
                              >
                                <Icon name="TrashIcon" size={14} />
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}

        {/* COUPONS TAB */}
        {activeTab === 'coupons' && (
          <div className="animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="font-display text-2xl font-semibold text-foreground">Coupons & Discounts</h2>
                <p className="text-sm text-muted mt-0.5">
                  {coupons.length} total · {activeCouponsCount} active · {coupons.filter(c => isCouponExpired(c)).length} expired
                </p>
              </div>
              <button
                onClick={() => { resetCouponForm(); setShowCouponModal(true); }}
                className="btn-primary px-4 py-2.5 text-sm flex items-center gap-2"
              >
                <Icon name="PlusIcon" size={16} variant="solid" /> Create Coupon
              </button>
            </div>

            {coupons.length === 0 ? (
              <div className="bg-white rounded-md border border-border text-center py-16">
                <Icon name="TagIcon" size={40} className="text-muted-light mx-auto mb-3" />
                <p className="text-sm font-medium text-foreground mb-1">No coupons yet</p>
                <p className="text-xs text-muted mb-4">Create your first coupon to offer discounts to customers</p>
                <button
                  onClick={() => { resetCouponForm(); setShowCouponModal(true); }}
                  className="btn-primary px-5 py-2.5 text-sm inline-flex items-center gap-2"
                >
                  <Icon name="PlusIcon" size={16} variant="solid" /> Create First Coupon
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {coupons.map(coupon => {
                  const expired = isCouponExpired(coupon);
                  const usagePct = coupon.usageLimit > 0 ? Math.min(100, (coupon.usedCount / coupon.usageLimit) * 100) : 0;
                  return (
                    <div
                      key={coupon.id}
                      className={`bg-white rounded-md border overflow-hidden transition-all ${
                        expired ? 'border-border opacity-70' : coupon.active ?'border-primary/30 shadow-sm' : 'border-border'
                      }`}
                    >
                      {/* Card header */}
                      <div className={`px-5 py-4 ${
                        expired ? 'bg-gray-50' : coupon.active ?'bg-amber-50' : 'bg-gray-50'
                      }`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono font-bold text-lg text-foreground tracking-wider">{coupon.code}</span>
                              <button
                                onClick={() => copyCouponCode(coupon)}
                                className="text-muted hover:text-primary transition-colors flex-shrink-0"
                                title="Copy code"
                              >
                                <Icon name={copiedCouponId === coupon.id ? 'CheckIcon' : 'ClipboardDocumentIcon'} size={15} />
                              </button>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${
                                expired ? 'bg-gray-100 text-gray-500' : coupon.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                              }`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${
                                  expired ? 'bg-gray-400' : coupon.active ? 'bg-green-500' : 'bg-red-500'
                                }`} />
                                {expired ? 'Expired' : coupon.active ? 'Active' : 'Inactive'}
                              </span>
                              <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                                coupon.discountType === 'percentage' ?'bg-blue-100 text-blue-700' :'bg-purple-100 text-purple-700'
                              }`}>
                                {coupon.discountType === 'percentage' ? `${coupon.discountValue}% OFF` : `₵${coupon.discountValue} OFF`}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              onClick={() => toggleCouponActive(coupon.id)}
                              disabled={expired}
                              className={`w-8 h-8 flex items-center justify-center rounded-sm transition-colors ${
                                expired ? 'text-muted-light cursor-not-allowed' :
                                coupon.active
                                  ? 'text-green-600 hover:bg-red-50 hover:text-danger' :'text-muted hover:bg-green-50 hover:text-green-600'
                              }`}
                              title={coupon.active ? 'Deactivate' : 'Activate'}
                            >
                              <Icon name={coupon.active ? 'CheckCircleIcon' : 'XCircleIcon'} size={18} />
                            </button>
                            <button
                              onClick={() => setDeleteCouponConfirm(coupon.id)}
                              className="w-8 h-8 flex items-center justify-center rounded-sm text-muted hover:bg-red-50 hover:text-danger transition-colors"
                              title="Delete coupon"
                            >
                              <Icon name="TrashIcon" size={16} />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Card body */}
                      <div className="px-5 py-4 space-y-3">
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div>
                            <p className="text-muted mb-0.5">Expires</p>
                            <p className={`font-medium ${expired ? 'text-danger' : 'text-foreground'}`}>
                              {new Date(coupon.expiryDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                          </div>
                          <div>
                            <p className="text-muted mb-0.5">Min. Order</p>
                            <p className="font-medium text-foreground">
                              {coupon.minOrderAmount > 0 ? `₵${coupon.minOrderAmount}` : 'No minimum'}
                            </p>
                          </div>
                        </div>

                        {/* Usage bar */}
                        <div>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-muted">Usage</span>
                            <span className="font-medium text-foreground">{coupon.usedCount} / {coupon.usageLimit}</span>
                          </div>
                          <div className="h-1.5 bg-border rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${
                                usagePct >= 90 ? 'bg-danger' : usagePct >= 60 ? 'bg-amber-400' : 'bg-success'
                              }`}
                              style={{ width: `${usagePct}%` }}
                            />
                          </div>
                        </div>

                        {/* Social share buttons */}
                        <div className="pt-1">
                          <p className="text-xs text-muted mb-2 font-medium">Share on social media</p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => shareCoupon(coupon, 'whatsapp')}
                              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-sm text-xs font-semibold bg-green-500 text-white hover:bg-green-600 transition-colors"
                              title="Share on WhatsApp"
                            >
                              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.198.298-.497.099-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 012.893 6.994c-.003 5.45 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                              </svg>
                              WhatsApp
                            </button>
                            <button
                              onClick={() => shareCoupon(coupon, 'facebook')}
                              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-sm text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                              title="Share on Facebook"
                            >
                              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.084v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                              </svg>
                              Facebook
                            </button>
                            <button
                              onClick={() => shareCoupon(coupon, 'twitter')}
                              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-sm text-xs font-semibold bg-black text-white hover:bg-gray-800 transition-colors"
                              title="Share on X (Twitter)"
                            >
                              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                              </svg>
                              X
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* SETTINGS TAB */}
        {activeTab === 'settings' && (
          <div className="animate-fade-in max-w-2xl">
            <div className="mb-6">
              <h2 className="font-display text-2xl font-semibold text-foreground">Account Settings</h2>
              <p className="text-sm text-muted mt-1">Manage your admin account credentials. All changes require current password confirmation.</p>
            </div>

            {/* Security notice */}
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-md px-4 py-3 mb-6">
              <Icon name="ShieldCheckIcon" size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Premium Security</p>
                <p className="text-xs text-amber-700 mt-0.5">All credential changes require your current password for verification. This protects your admin account from unauthorized modifications.</p>
              </div>
            </div>

            {/* Section switcher */}
            <div className="flex gap-1 mb-6 bg-accent rounded-sm p-1 w-fit">
              <button
                onClick={() => { setSettingsSection('account'); setAccountError(''); setAccountSuccess(''); }}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-sm text-sm font-medium transition-all ${settingsSection === 'account' ? 'bg-white text-foreground shadow-sm' : 'text-muted hover:text-foreground'}`}
              >
                <Icon name="UserCircleIcon" size={16} />
                Username
              </button>
              <button
                onClick={() => { setSettingsSection('password'); setPasswordError(''); setPasswordSuccess(''); }}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-sm text-sm font-medium transition-all ${settingsSection === 'password' ? 'bg-white text-foreground shadow-sm' : 'text-muted hover:text-foreground'}`}
              >
                <Icon name="LockClosedIcon" size={16} />
                Password
              </button>
            </div>

            {/* Current account info */}
            <div className="bg-white border border-border rounded-md p-4 mb-6 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="UserCircleIcon" size={22} className="text-primary" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">{adminCreds.username}</p>
                <p className="text-xs text-muted">Administrator · Single account</p>
              </div>
              <div className="ml-auto">
                <span className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded-full px-2.5 py-1 font-medium">
                  <Icon name="ShieldCheckIcon" size={12} />
                  Active
                </span>
              </div>
            </div>

            {/* UPDATE USERNAME */}
            {settingsSection === 'account' && (
              <div className="bg-white border border-border rounded-md overflow-hidden">
                <div className="px-5 py-4 border-b border-border bg-accent/40">
                  <h3 className="font-semibold text-sm text-foreground flex items-center gap-2">
                    <Icon name="UserCircleIcon" size={16} className="text-primary" />
                    Update Username
                  </h3>
                  <p className="text-xs text-muted mt-0.5">Change your admin login username</p>
                </div>
                <form onSubmit={handleSaveAccount} className="p-5 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      New Username <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      value={accountForm.newUsername}
                      onChange={e => setAccountForm(f => ({ ...f, newUsername: e.target.value }))}
                      placeholder="Enter new username"
                      className="form-input"
                      autoComplete="off"
                    />
                    <p className="text-xs text-muted mt-1">Min. 3 characters. Letters, numbers, and underscores only.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      Current Password <span className="text-danger">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showAccountCurrentPass ? 'text' : 'password'}
                        value={accountForm.currentPasswordForAccount}
                        onChange={e => setAccountForm(f => ({ ...f, currentPasswordForAccount: e.target.value }))}
                        placeholder="Enter current password to confirm"
                        className="form-input pr-10"
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowAccountCurrentPass(v => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-foreground transition-colors"
                      >
                        <Icon name={showAccountCurrentPass ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                      </button>
                    </div>
                    <p className="text-xs text-muted mt-1 flex items-center gap-1">
                      <Icon name="LockClosedIcon" size={11} />
                      Required for security verification
                    </p>
                  </div>

                  {accountError && (
                    <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-sm px-3 py-2.5 text-sm text-danger">
                      <Icon name="ExclamationCircleIcon" size={16} />
                      {accountError}
                    </div>
                  )}
                  {accountSuccess && (
                    <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-sm px-3 py-2.5 text-sm text-success">
                      <Icon name="CheckCircleIcon" size={16} />
                      {accountSuccess}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={accountLoading}
                    className="btn-primary px-6 py-2.5 text-sm flex items-center gap-2 disabled:opacity-70"
                  >
                    {accountLoading ? (
                      <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>
                    ) : (
                      <><Icon name="CheckIcon" size={16} /> Save Username</>
                    )}
                  </button>
                </form>
              </div>
            )}

            {/* RESET PASSWORD */}
            {settingsSection === 'password' && (
              <div className="bg-white border border-border rounded-md overflow-hidden">
                <div className="px-5 py-4 border-b border-border bg-accent/40">
                  <h3 className="font-semibold text-sm text-foreground flex items-center gap-2">
                    <Icon name="LockClosedIcon" size={16} className="text-primary" />
                    Reset Password
                  </h3>
                  <p className="text-xs text-muted mt-0.5">Update your admin account password</p>
                </div>
                <form onSubmit={handleSavePassword} className="p-5 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      Current Password <span className="text-danger">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showCurrentPass ? 'text' : 'password'}
                        value={passwordForm.currentPassword}
                        onChange={e => setPasswordForm(f => ({ ...f, currentPassword: e.target.value }))}
                        placeholder="Enter your current password"
                        className="form-input pr-10"
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPass(v => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-foreground transition-colors"
                      >
                        <Icon name={showCurrentPass ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      New Password <span className="text-danger">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPass ? 'text' : 'password'}
                        value={passwordForm.newPassword}
                        onChange={e => setPasswordForm(f => ({ ...f, newPassword: e.target.value }))}
                        placeholder="Enter new password"
                        className="form-input pr-10"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPass(v => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-foreground transition-colors"
                      >
                        <Icon name={showNewPass ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                      </button>
                    </div>
                    {passwordForm.newPassword && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-muted">Password strength</span>
                          <span className={`text-xs font-medium ${passwordStrength.label === 'Strong' ? 'text-green-600' : passwordStrength.label === 'Fair' ? 'text-amber-600' : 'text-red-600'}`}>{passwordStrength.label}</span>
                        </div>
                        <div className="h-1.5 bg-border rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${passwordStrength.color}`} style={{ width: passwordStrength.width }} />
                        </div>
                      </div>
                    )}
                    <p className="text-xs text-muted mt-1.5">Min. 8 characters, at least one uppercase letter and one number.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      Confirm New Password <span className="text-danger">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPass ? 'text' : 'password'}
                        value={passwordForm.confirmPassword}
                        onChange={e => setPasswordForm(f => ({ ...f, confirmPassword: e.target.value }))}
                        placeholder="Re-enter new password"
                        className="form-input pr-10"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPass(v => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-foreground transition-colors"
                      >
                        <Icon name={showConfirmPass ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                      </button>
                    </div>
                    {passwordForm.confirmPassword && passwordForm.newPassword && (
                      <p className={`text-xs mt-1 flex items-center gap-1 ${passwordForm.newPassword === passwordForm.confirmPassword ? 'text-success' : 'text-danger'}`}>
                        <Icon name={passwordForm.newPassword === passwordForm.confirmPassword ? 'CheckCircleIcon' : 'XCircleIcon'} size={12} />
                        {passwordForm.newPassword === passwordForm.confirmPassword ? 'Passwords match' : 'Passwords do not match'}
                      </p>
                    )}
                  </div>

                  {passwordError && (
                    <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-sm px-3 py-2.5 text-sm text-danger">
                      <Icon name="ExclamationCircleIcon" size={16} />
                      {passwordError}
                    </div>
                  )}
                  {passwordSuccess && (
                    <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-sm px-3 py-2.5 text-sm text-success">
                      <Icon name="CheckCircleIcon" size={16} />
                      {passwordSuccess}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={passwordLoading}
                    className="btn-primary px-6 py-2.5 text-sm flex items-center gap-2 disabled:opacity-70"
                  >
                    {passwordLoading ? (
                      <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Updating...</>
                    ) : (
                      <><Icon name="LockClosedIcon" size={16} /> Update Password</>
                    )}
                  </button>
                </form>
              </div>
            )}
          </div>
        )}

        {/* Coupon Create Modal */}
        {showCouponModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-md border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto animate-scale-in">
              <div className="px-6 py-4 border-b border-border flex items-center justify-between sticky top-0 bg-white z-10">
                <h3 className="font-display text-lg font-semibold text-foreground">Create New Coupon</h3>
                <button onClick={() => setShowCouponModal(false)} className="text-muted-light hover:text-foreground transition-colors">
                  <Icon name="XMarkIcon" size={20} />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* Coupon Code */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Coupon Code *</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponForm.code || ''}
                      onChange={e => setCouponForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                      placeholder="e.g. CAKE20OFF"
                      className="form-input flex-1 font-mono uppercase"
                      maxLength={20}
                    />
                    <button
                      type="button"
                      onClick={() => setCouponForm(f => ({ ...f, code: generateCouponCode() }))}
                      className="btn-outline px-3 py-2 text-sm flex items-center gap-1.5 whitespace-nowrap"
                    >
                      <Icon name="ArrowPathIcon" size={14} /> Auto
                    </button>
                  </div>
                  <p className="text-xs text-muted mt-1">Customers will enter this code at checkout. Uppercase letters and numbers only.</p>
                </div>

                {/* Discount Type */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Discount Type *</label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { value: 'percentage', label: 'Percentage (%)', icon: 'ReceiptPercentIcon' },
                      { value: 'fixed', label: 'Fixed Amount (₵)', icon: 'BanknotesIcon' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setCouponForm(f => ({ ...f, discountType: opt.value as 'percentage' | 'fixed' }))}
                        className={`flex items-center gap-2 p-3 rounded-sm border-2 text-sm font-medium transition-all ${
                          couponForm.discountType === opt.value
                            ? 'border-primary bg-amber-50 text-primary' :'border-border text-muted hover:border-muted-light'
                        }`}
                      >
                        <Icon name={opt.icon as Parameters<typeof Icon>[0]['name']} size={16} />
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Discount Value */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Discount Value * {couponForm.discountType === 'percentage' ? '(%)' : '(₵)'}
                  </label>
                  <input
                    type="number"
                    value={couponForm.discountValue || ''}
                    onChange={e => setCouponForm(f => ({ ...f, discountValue: parseFloat(e.target.value) || 0 }))}
                    placeholder={couponForm.discountType === 'percentage' ? 'e.g. 20' : 'e.g. 15'}
                    min="0"
                    max={couponForm.discountType === 'percentage' ? 100 : undefined}
                    step="0.01"
                    className="form-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Expiry Date */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Expiry Date *</label>
                    <input
                      type="date"
                      value={couponForm.expiryDate || ''}
                      onChange={e => setCouponForm(f => ({ ...f, expiryDate: e.target.value }))}
                      min={new Date().toISOString().split('T')[0]}
                      className="form-input"
                    />
                  </div>

                  {/* Usage Limit */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Usage Limit</label>
                    <input
                      type="number"
                      value={couponForm.usageLimit || ''}
                      onChange={e => setCouponForm(f => ({ ...f, usageLimit: parseInt(e.target.value) || 0 }))}
                      placeholder="100"
                      min="1"
                      className="form-input"
                    />
                  </div>
                </div>

                {/* Min Order Amount */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Minimum Order Amount (₵)</label>
                  <input
                    type="number"
                    value={couponForm.minOrderAmount || ''}
                    onChange={e => setCouponForm(f => ({ ...f, minOrderAmount: parseFloat(e.target.value) || 0 }))}
                    placeholder="0 (no minimum)"
                    min="0"
                    step="0.01"
                    className="form-input"
                  />
                  <p className="text-xs text-muted mt-1">Leave as 0 for no minimum order requirement.</p>
                </div>

                {/* Active toggle */}
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setCouponForm(f => ({ ...f, active: !f.active }))}
                    className={`relative w-10 h-6 rounded-full transition-all duration-200 flex-shrink-0 ${
                      couponForm.active ? 'bg-primary' : 'bg-border'
                    }`}
                  >
                    <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all duration-200 ${
                      couponForm.active ? 'left-4.5 translate-x-0.5' : 'left-0.5'
                    }`} style={{ left: couponForm.active ? '18px' : '2px' }} />
                  </button>
                  <label className="text-sm font-medium text-foreground">Activate coupon immediately</label>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-border flex gap-3">
                <button onClick={() => setShowCouponModal(false)} className="btn-outline flex-1 py-2.5 text-sm">Cancel</button>
                <button
                  onClick={saveCoupon}
                  className="btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-2"
                >
                  <Icon name="TagIcon" size={16} /> Create Coupon
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Coupon Confirm Modal */}
        {deleteCouponConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-md border border-border w-full max-w-sm p-6 animate-scale-in">
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                <Icon name="ExclamationTriangleIcon" size={24} className="text-danger" />
              </div>
              <h3 className="font-display text-lg font-semibold text-foreground text-center mb-2">Delete Coupon?</h3>
              <p className="text-sm text-muted text-center mb-6">This action cannot be undone. The coupon will be permanently removed.</p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteCouponConfirm(null)} className="btn-outline flex-1 py-2.5 text-sm">Cancel</button>
                <button
                  onClick={() => deleteCoupon(deleteCouponConfirm)}
                  className="flex-1 py-2.5 text-sm font-semibold bg-danger text-white rounded-sm hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Product Modal */}
        {showProductModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-md border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto animate-scale-in">
              <div className="px-6 py-4 border-b border-border flex items-center justify-between sticky top-0 bg-white z-10">
                <h3 className="font-display text-lg font-semibold text-foreground">
                  {editingProduct ? 'Edit Product' : 'Add New Product'}
                </h3>
                <button onClick={() => setShowProductModal(false)} className="text-muted-light hover:text-foreground transition-colors">
                  <Icon name="XMarkIcon" size={20} />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Product Name *</label>
                  <input
                    type="text"
                    value={productForm.name || ''}
                    onChange={e => setProductForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Nozzle Set (12pcs)"
                    className="form-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Price (₵) *</label>
                    <input
                      type="number"
                      value={productForm.price || ''}
                      onChange={e => setProductForm(f => ({ ...f, price: parseFloat(e.target.value) || 0 }))}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      className="form-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Stock Quantity</label>
                    <input
                      type="number"
                      value={productForm.stock ?? ''}
                      onChange={e => setProductForm(f => ({ ...f, stock: parseInt(e.target.value) || 0 }))}
                      placeholder="0"
                      min="0"
                      className="form-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Category</label>
                  <select
                    value={productForm.category || categories[0]}
                    onChange={e => setProductForm(f => ({ ...f, category: e.target.value }))}
                    className="form-input"
                  >
                    {categories.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Short Description</label>
                  <input
                    type="text"
                    value={productForm.shortDescription || ''}
                    onChange={e => setProductForm(f => ({ ...f, shortDescription: e.target.value }))}
                    placeholder="Brief product summary"
                    className="form-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Full Description</label>
                  <textarea
                    value={productForm.description || ''}
                    onChange={e => setProductForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Detailed product description..."
                    rows={3}
                    className="form-input resize-none"
                  />
                </div>

                {/* Product Image */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Product Image</label>
                  {/* Toggle between URL and Upload */}
                  <div className="flex gap-1 mb-3 bg-accent border border-border rounded-sm p-1 w-fit">
                    <button
                      type="button"
                      onClick={() => setImageUploadMode('url')}
                      className={`px-3 py-1.5 rounded-sm text-xs font-medium transition-all ${
                        imageUploadMode === 'url' ?'bg-white text-foreground shadow-sm' : 'text-muted hover:text-foreground'
                      }`}
                    >
                      Image URL
                    </button>
                    <button
                      type="button"
                      onClick={() => setImageUploadMode('upload')}
                      className={`px-3 py-1.5 rounded-sm text-xs font-medium transition-all ${
                        imageUploadMode === 'upload' ? 'bg-white text-foreground shadow-sm' : 'text-muted hover:text-foreground'
                      }`}
                    >
                      Upload from Computer
                    </button>
                  </div>

                  {imageUploadMode === 'url' ? (
                    <input
                      type="url"
                      value={imageUploadMode === 'url' ? (productForm.image?.startsWith('data:') ? '' : productForm.image || '') : ''}
                      onChange={e => {
                        setProductForm(f => ({ ...f, image: e.target.value }));
                        setImagePreview(e.target.value);
                      }}
                      placeholder="https://images.unsplash.com/..."
                      className="form-input"
                    />
                  ) : (
                    <div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageFileChange}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full border-2 border-dashed border-border rounded-sm py-6 flex flex-col items-center gap-2 text-muted hover:border-primary hover:text-primary transition-colors cursor-pointer bg-accent"
                      >
                        <Icon name="ArrowUpTrayIcon" size={24} />
                        <span className="text-sm font-medium">Click to upload image</span>
                        <span className="text-xs">PNG, JPG, WEBP up to 5MB</span>
                      </button>
                    </div>
                  )}

                  {/* Image Preview */}
                  {imagePreview && (
                    <div className="mt-3 flex items-center gap-3">
                      <div className="w-16 h-16 rounded-sm overflow-hidden border border-border flex-shrink-0">
                        <AppImage src={imagePreview} alt="Preview" width={64} height={64} className="object-cover w-full h-full" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-muted">Image preview</p>
                        <button
                          type="button"
                          onClick={() => {
                            setImagePreview('');
                            setProductForm(f => ({ ...f, image: '' }));
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                          className="text-xs text-danger hover:underline mt-0.5"
                        >
                          Remove image
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setProductForm(f => ({ ...f, featured: !f.featured }))}
                    className={`relative w-10 h-6 rounded-full transition-all duration-200 flex-shrink-0 ${
                      productForm.featured ? 'bg-primary' : 'bg-border'
                    }`}
                  >
                    <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all duration-200 ${
                      productForm.featured ? 'left-4.5 translate-x-0.5' : 'left-0.5'
                    }`} style={{ left: productForm.featured ? '18px' : '2px' }} />
                  </button>
                  <label className="text-sm font-medium text-foreground">Mark as Featured Product</label>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-border flex gap-3">
                <button
                  onClick={() => setShowProductModal(false)}
                  className="btn-outline flex-1 py-2.5 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={saveProduct}
                  disabled={!productForm.name || !productForm.price}
                  className="btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Icon name="CheckIcon" size={16} variant="solid" />
                  {editingProduct ? 'Save Changes' : 'Add Product'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirm Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-md border border-border w-full max-w-sm p-6 animate-scale-in">
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                <Icon name="ExclamationTriangleIcon" size={24} className="text-danger" />
              </div>
              <h3 className="font-display text-lg font-semibold text-foreground text-center mb-2">Delete Product?</h3>
              <p className="text-sm text-muted text-center mb-6">This action cannot be undone. The product will be permanently removed.</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="btn-outline flex-1 py-2.5 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteProduct(deleteConfirm)}
                  className="flex-1 py-2.5 text-sm font-semibold bg-danger text-white rounded-sm hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="border-t border-border mt-auto">
          <div className="max-w-container mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <p className="text-xs text-muted">© 2026 CakePalace Admin</p>
            <div className="flex items-center gap-4">
              <Link href="/product-shop" className="text-xs text-muted hover:text-primary transition-colors">View Shop</Link>
              <button onClick={handleLogout} className="text-xs text-muted hover:text-danger transition-colors">Logout</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}